/**
 * avatarFallback.ts: a profile photo that will not load becomes the person's
 * initials, not the browser's broken-image glyph.
 *
 * `avatarHTML` (data.js) is the one renderer behind all twenty avatar sites in
 * app.js. It decides photo-or-initials ONCE, at render time, from whether the
 * row has an avatar URL. Nothing checked that the URL loads. A rotted link, a
 * host the image policy refuses (Google profile photos were refused from
 * 2026-09-16 to 2026-09-19), or an image that was deleted all painted the
 * glyph, on every card, comment and profile that person appears on (card
 * 1218624237210752).
 *
 * WHAT THIS DOES
 *  1. Hears the failed load. `error` does not bubble, so the listener is a
 *     CAPTURE listener on the document, and it acts only on an <img> directly
 *     inside his photo wrapper (`.avatar-img[data-gouser]`). A listing photo
 *     that fails is not ours and is left alone.
 *  2. Tells the bridge (`__gbzAvatarFailed`), which puts the URL aside on the
 *     row. From then on HIS renderer draws the initials on every repaint, so
 *     there is no flash of the glyph and nothing of ours to re-apply.
 *  3. Fixes the avatars already on screen, IN PLACE. His wire() binds the
 *     "open this profile" click to each avatar NODE, so replacing the node
 *     would leave initials that do nothing when tapped until the next paint.
 *     The wrapper keeps its node, its handle and its size class; it loses
 *     `avatar-img` and its <img>, and gains the text his renderer gives for a
 *     row with no photo. That text is read out of `avatarHTML` itself, so the
 *     markup is never written twice. On a profile head, losing `avatar-img` is
 *     also what makes the avatar inert instead of opening the photo zoom.
 *  4. Remembers the URLs that failed. A floor refresh installs fresh rows and
 *     brings a bad URL back; an observer on #app swaps a known-bad photo the
 *     moment it is painted, without waiting for it to fail a second time.
 *  5. Catches a failure that happened BEFORE it was installed (the first paint
 *     runs before the fixups do): an avatar image that is complete with no
 *     width is given its own src again so the browser reports the failure
 *     again, this time with someone listening. Once per ADDRESS for the life of
 *     the page, not once per element: his render() makes new elements on every
 *     repaint, and an image that is complete with no width and never errors (an
 *     SVG with no size of its own) would otherwise be retried on every one of
 *     them. A healthy image comes back from cache. jsdom loads no images, so
 *     there it does nothing.
 *
 * A failure is remembered for the life of the page. One that was only a bad
 * moment on the network stays as initials until the next full load, which
 * tries the photo again. Initials over a photo that might have loaded is the
 * cheaper mistake than a broken glyph over a person's name.
 */

export const AVATAR_PHOTO_SELECTOR = ".avatar-img[data-gouser]";

interface AvatarWindow extends Window {
  /** data.js's top-level function: a window property because data.js is a classic script. */
  avatarHTML?: (handle: string, size?: string) => string;
  __gbzAvatarFailed?: (handle: string) => boolean;
}

export function installAvatarFallback(win: Window): () => void {
  const doc = win.document;
  const w = win as AvatarWindow;
  /** Photo URLs that failed on this page, as written in the src attribute. */
  const failed = new Set<string>();
  /** Addresses already given their one retry (see 5 in the header). */
  const retried = new Set<string>();

  const sizeOf = (wrap: Element): string | undefined =>
    wrap.classList.contains("avatar-xl") ? "xl" : wrap.classList.contains("avatar-sm") ? "sm" : undefined;

  /** What his renderer draws for this handle now that the row has no photo. */
  function initialsFor(handle: string, size: string | undefined): { className: string; text: string } | null {
    if (typeof w.avatarHTML !== "function") return null;
    const holder = doc.createElement("div");
    holder.innerHTML = w.avatarHTML(handle, size);
    const fresh = holder.firstElementChild;
    /* Still a photo: the bridge is missing or did not know the handle. There is
       nothing honest to draw from here, so the avatar is left as it is. */
    if (!fresh || fresh.querySelector("img")) return null;
    return { className: fresh.className, text: fresh.textContent ?? "" };
  }

  function toInitials(wrap: HTMLElement): void {
    const handle = wrap.dataset.gouser;
    if (!handle) return;
    w.__gbzAvatarFailed?.(handle);
    const drawn = initialsFor(handle, sizeOf(wrap));
    if (!drawn) return;
    wrap.className = drawn.className;
    wrap.textContent = drawn.text;
  }

  /** Every photo avatar on screen for this handle: the same person is usually on the page more than once. */
  function swapAllFor(handle: string): void {
    for (const el of Array.from(doc.querySelectorAll<HTMLElement>(AVATAR_PHOTO_SELECTOR))) {
      if (el.dataset.gouser === handle) toInitials(el);
    }
  }

  const photoOf = (wrap: Element): HTMLImageElement | null => {
    const img = wrap.firstElementChild;
    return img && img.tagName === "IMG" ? (img as HTMLImageElement) : null;
  };

  const onError = (event: Event): void => {
    const img = event.target as Element | null;
    if (!img || img.tagName !== "IMG") return;
    const wrap = img.parentElement;
    if (!wrap || !wrap.matches(AVATAR_PHOTO_SELECTOR)) return;
    const src = img.getAttribute("src");
    if (src) failed.add(src);
    const handle = (wrap as HTMLElement).dataset.gouser;
    if (handle) swapAllFor(handle);
  };

  /** After each of his repaints: known-bad photos go at once, and unloaded ones get their one retry. */
  function sweep(): void {
    for (const wrap of Array.from(doc.querySelectorAll<HTMLElement>(AVATAR_PHOTO_SELECTOR))) {
      const img = photoOf(wrap);
      if (!img) continue;
      const src = img.getAttribute("src");
      if (src && failed.has(src)) {
        toInitials(wrap);
        continue;
      }
      if (src && img.complete && img.naturalWidth === 0 && !retried.has(src)) {
        retried.add(src);
        img.setAttribute("src", src);
      }
    }
  }

  doc.addEventListener("error", onError, true);
  const observer = new MutationObserver(sweep);
  const root = doc.getElementById("app") ?? doc.body;
  if (root) observer.observe(root, { childList: true, subtree: true });
  sweep();

  return () => {
    observer.disconnect();
    doc.removeEventListener("error", onError, true);
  };
}
